/**
 * The phone's side of the translate edge function (Section 10).
 *
 * Every failure comes back as a value, never a throw, because the two ways to
 * fail here are both ordinary on the island: no signal, and a spent allowance.
 * The caller decides what to say and never loses the text.
 */

import { functionHeaders } from '@dia/db';

export type TranslateOk = { ok: true; text: string; cached: boolean };
export type TranslateFail = {
  ok: false;
  reason: 'offline' | 'quota' | 'session' | 'failed';
  message?: { es: string; en: string };
};
export type TranslateResult = TranslateOk | TranslateFail;

export type TranslateArgs = {
  url: string;
  anonKey: string;
  token: string;
  text: string;
  from: 'es' | 'en';
  to: 'es' | 'en';
  audience: 'staff' | 'guest' | 'owner';
  /** Give up after this long. Starlink can hang instead of failing. */
  timeoutMs?: number;
};

export async function translateText(args: TranslateArgs): Promise<TranslateResult> {
  const online = typeof navigator === 'undefined' ? true : navigator.onLine;
  if (!online) return { ok: false, reason: 'offline' };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), args.timeoutMs ?? 12_000);

  try {
    const response = await fetch(`${args.url}/functions/v1/translate`, {
      method: 'POST',
      signal: controller.signal,
      headers: functionHeaders({ anonKey: args.anonKey, token: args.token }),
      body: JSON.stringify({
        text: args.text,
        source_lang: args.from,
        target_lang: args.to,
        audience: args.audience,
      }),
    });

    const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;

    if (response.ok && body && typeof body.text === 'string') {
      return { ok: true, text: body.text, cached: body.cached === true };
    }

    const message =
      body && typeof body.message_es === 'string' && typeof body.message_en === 'string'
        ? { es: body.message_es, en: body.message_en }
        : undefined;
    if (response.status === 429) return { ok: false, reason: 'quota', message };
    if (response.status === 401) return { ok: false, reason: 'session', message };
    return { ok: false, reason: 'failed', message };
  } catch {
    return { ok: false, reason: 'offline' };
  } finally {
    clearTimeout(timer);
  }
}
